/**
 * Validation utilities for form fields
 * Used for teacher-side student profile editing
 */

export interface ValidationResult {
  isValid: boolean
  error: string | null
}

// ─── Disposable / temporary email domain blocklist ──────────────────────
// Static list of well-known throwaway email providers. Compared case-
// insensitively against the domain portion of the address (the part after
// the final "@"). This list is intentionally curated rather than
// exhaustive — it covers the most common services used to bypass signup
// gates without bloating the bundle. Add new domains alphabetically.
const DISPOSABLE_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  '0-mail.com',
  '10minutemail.com',
  '10minutemail.net',
  '20minutemail.com',
  'anonbox.net',
  'anonymbox.com',
  'binkmail.com',
  'bouncr.com',
  'burnermail.io',
  'byom.de',
  'deadaddress.com',
  'discard.email',
  'discardmail.com',
  'dispostable.com',
  'dropmail.me',
  'duck.com',
  'einrot.com',
  'emailondeck.com',
  'emailsensei.com',
  'emltmp.com',
  'fakeinbox.com',
  'fakemail.net',
  'fakemailgenerator.com',
  'fakermail.com',
  'fastmail.cn',
  'getairmail.com',
  'getnada.com',
  'grr.la',
  'guerrillamail.biz',
  'guerrillamail.com',
  'guerrillamail.de',
  'guerrillamail.info',
  'guerrillamail.net',
  'guerrillamail.org',
  'guerrillamailblock.com',
  'harakirimail.com',
  'incognitomail.com',
  'inboxbear.com',
  'inboxkitten.com',
  'jetable.org',
  'mail-temp.com',
  'mail-temporaire.com',
  'mail-temporaire.fr',
  'mail7.io',
  'mailbox52.ga',
  'maildrop.cc',
  'mailcatch.com',
  'mailde.de',
  'maildim.com',
  'mailforspam.com',
  'mailinator.com',
  'mailinator.net',
  'mailinator.org',
  'mailnator.com',
  'mailnesia.com',
  'mailpoof.com',
  'mailsac.com',
  'mailtemp.info',
  'mintemail.com',
  'mohmal.com',
  'moakt.com',
  'mt2014.com',
  'mvrht.com',
  'mytemp.email',
  'mytrashmail.com',
  'nada.email',
  'no-spam.ws',
  'nowmymail.com',
  'objectmail.com',
  'one-time.email',
  'onetimeemail.net',
  'rcpt.at',
  'rhyta.com',
  'sharklasers.com',
  'sneakemail.com',
  'spam4.me',
  'spambog.com',
  'spambox.us',
  'spamgourmet.com',
  'spamherelots.com',
  'temp-mail.io',
  'temp-mail.org',
  'tempail.com',
  'tempmail.com',
  'tempmail.de',
  'tempmail.dev',
  'tempmail.net',
  'tempmail.plus',
  'tempmailer.com',
  'tempmailo.com',
  'tempr.email',
  'throwam.com',
  'throwawaymail.com',
  'tmail.ws',
  'tmpmail.net',
  'tmpmail.org',
  'trashmail.com',
  'trashmail.de',
  'trashmail.io',
  'trashmail.me',
  'trashmail.net',
  'trbvm.com',
  'wegwerfemail.de',
  'wegwerfmail.de',
  'yopmail.com',
  'yopmail.fr',
  'yopmail.net',
  'zetmail.com',
])

/**
 * Returns true if the given email's domain is on the disposable / temporary
 * email blocklist. Returns false for empty / malformed input — format
 * validation is the caller's responsibility (use validateEmail for that).
 */
export const isDisposableEmail = (email: string): boolean => {
  if (!email) return false
  const trimmed = email.trim().toLowerCase()
  const atIndex = trimmed.lastIndexOf('@')
  if (atIndex === -1 || atIndex === trimmed.length - 1) return false
  const domain = trimmed.slice(atIndex + 1)
  return DISPOSABLE_EMAIL_DOMAINS.has(domain)
}

/**
 * Validate email address using RFC 5322 compliant regex
 * @param email - Email address to validate
 * @returns Validation result with error message if invalid
 */
export const validateEmail = (email: string): ValidationResult => {
  // Allow empty email (optional field)
  if (!email || email.trim() === '') {
    return { isValid: true, error: null }
  }

  const trimmedEmail = email.trim()

  // Strict email validation regex - requires proper domain with TLD
  // Format: localpart@domain.tld
  const strictEmailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/

  if (!strictEmailRegex.test(trimmedEmail)) {
    return {
      isValid: false,
      error: 'Please enter a valid email address (e.g., name@example.com)',
    }
  }

  // Additional checks
  const parts = trimmedEmail.split('@')
  if (parts.length !== 2) {
    return {
      isValid: false,
      error: 'Email must contain exactly one @ symbol',
    }
  }

  const [localPart, domain] = parts

  // Check local part length
  if (localPart.length === 0 || localPart.length > 64) {
    return {
      isValid: false,
      error: 'Email address format is invalid',
    }
  }

  // Check domain has at least one dot
  if (!domain.includes('.')) {
    return {
      isValid: false,
      error: 'Email domain must include a valid extension (e.g., .com, .org)',
    }
  }

  // Check domain parts
  const domainParts = domain.split('.')
  const tld = domainParts[domainParts.length - 1]

  if (tld.length < 2) {
    return {
      isValid: false,
      error: 'Email domain extension must be at least 2 characters',
    }
  }

  // Final check: reject known disposable / temporary email providers.
  // Runs last so legitimate format errors surface first with their existing
  // messages, and only well-formed addresses are tested against the blocklist.
  if (isDisposableEmail(trimmedEmail)) {
    return {
      isValid: false,
      error:
        'Please use a permanent email address. Temporary/disposable email providers are not allowed.',
    }
  }

  return { isValid: true, error: null }
}

/**
 * Validate phone number
 * Supports US format and international E.164 format
 * @param phone - Phone number to validate
 * @returns Validation result with error message if invalid
 */
export const validatePhone = (phone: string): ValidationResult => {
  // Allow empty phone (optional field)
  if (!phone || phone.trim() === '') {
    return { isValid: true, error: null }
  }

  const trimmedPhone = phone.trim()

  // Remove common formatting characters for validation
  const digitsOnly = trimmedPhone.replace(/[\s\-\(\)\+\.]/g, '')

  // Check if contains only digits (after removing formatting)
  if (!/^\d+$/.test(digitsOnly)) {
    return {
      isValid: false,
      error: 'Phone number can only contain digits and formatting characters',
    }
  }

  // International format: starts with +, followed by country code and number
  // E.164 format: +[1-3 digit country code][up to 15 digits]
  if (trimmedPhone.startsWith('+')) {
    // E.164 validation
    if (digitsOnly.length < 10 || digitsOnly.length > 15) {
      return {
        isValid: false,
        error: 'International phone number must be between 10-15 digits',
      }
    }
    return { isValid: true, error: null }
  }

  // US format validation: must have at least 10 digits
  if (digitsOnly.length < 10) {
    return {
      isValid: false,
      error: 'Phone number must be at least 10 digits',
    }
  }

  // US format: exactly 10 or 11 digits (with country code)
  if (digitsOnly.length > 11) {
    return {
      isValid: false,
      error: 'US phone number must be 10 digits (or 11 with country code)',
    }
  }

  // If 11 digits, first digit should be 1 (US country code)
  if (digitsOnly.length === 11 && digitsOnly[0] !== '1') {
    return {
      isValid: false,
      error: 'Invalid country code for US phone number',
    }
  }

  return { isValid: true, error: null }
}

/**
 * Validate all student profile fields
 * Used before saving to ensure data integrity
 */
export interface StudentProfileValidation {
  email: ValidationResult
  phone: ValidationResult
  guardianEmail: ValidationResult
  guardianPhone: ValidationResult
  isValid: boolean
}

export const validateStudentProfile = (data: {
  email?: string
  phone?: string
  guardianEmail?: string
  guardianPhone?: string
}): StudentProfileValidation => {
  const emailValidation = validateEmail(data.email || '')
  const phoneValidation = validatePhone(data.phone || '')
  const guardianEmailValidation = validateEmail(data.guardianEmail || '')
  const guardianPhoneValidation = validatePhone(data.guardianPhone || '')

  return {
    email: emailValidation,
    phone: phoneValidation,
    guardianEmail: guardianEmailValidation,
    guardianPhone: guardianPhoneValidation,
    isValid:
      emailValidation.isValid &&
      phoneValidation.isValid &&
      guardianEmailValidation.isValid &&
      guardianPhoneValidation.isValid,
  }
}
